/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RuleSetFilters.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
"use strict";

import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import CustomFilterV2 from "../../../components/customComponentsV2/common/filter/CustomFilterV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";

/**
 * RuleSetFilters
 *
 * Pure UI + filter state adapter for Rule Center list API
 *
 * Expected API compatibility:
 * - sortBy        -> order_by_column
 * - sortOrder     -> order_by_sort
 * - status        -> status
 * - hasWorkflow   -> has_workflow
 */
function RuleSetFilters({
  anchorEl,
  open,
  onOpen,
  onClose,
  filters,
  setFilters,
  onApply,
  onClearAll,
}) {
  return (
    <CustomFilterV2
      anchorEl={anchorEl}
      open={open}
      widthPerColumn="250px"
      handleClick={onOpen}
      handleClose={onClose}
      handleFilterButton={(action) => {
        if (action === "apply") {
          onApply();
        }
        if (action === "clear") {
          onClearAll();
        }
        if (action === "cancel") {
          onClose();
        }
      }}
      showClearAll={true}
      handleClearAll={onClearAll}
      sections={[
        // ======================= SORTING =======================
        {
          heading: "Sorting",
          content: (
            <Box>
              <Box sx={{ p: 2 }}>
                <CustomRadioButtonV2
                  value={filters.sortBy}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      sortBy: e.target.value,
                    }))
                  }
                  data={[
                    { value: "created_at", label: "Created At" },
                    { value: "updated_at", label: "Updated At" },
                  ]}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  px: 2,
                }}
              >
                {/* ASC */}
                <Box
                  role="button"
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      sortOrder: "ASC",
                    }))
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    px: 1.5,
                    py: 0.75,
                    backgroundColor:
                      filters.sortOrder === "ASC" ? "#26890D" : "transparent",
                    color: filters.sortOrder === "ASC" ? "#FFFFFF" : "#000000",
                  }}
                >
                  <Typography variant="body2">Sort Asc – Desc</Typography>
                </Box>

                {/* DESC */}
                <Box
                  role="button"
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      sortOrder: "DESC",
                    }))
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    px: 1.5,
                    py: 0.75,
                    backgroundColor:
                      filters.sortOrder === "DESC" ? "#26890D" : "transparent",
                    color: filters.sortOrder === "DESC" ? "#FFFFFF" : "#000000",
                  }}
                >
                  <Typography variant="body2">Sort Desc – Asc</Typography>
                </Box>
              </Box>
            </Box>
          ),
        },

        // ======================= WORKFLOW =======================
        {
          heading: "Workflow",
          content: (
            <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
              <CustomCheckBoxV2
                label="Has Workflow"
                value={filters.hasWorkflow === true}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    hasWorkflow: e.target.checked ? true : null,
                  }))
                }
              />
            </Box>
          ),
        },
      ]}
    />
  );
}

RuleSetFilters.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
    status: PropTypes.string,
    hasWorkflow: PropTypes.oneOf([true, false, null]),
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
};

export default RuleSetFilters;
